import { LocalAtRecord } from '../db';
import { JobsFieldSet, ShiftsFieldSet } from '@rogoag/airtable/crm';
import { SkipsProcessingJob } from './domain/SkipsProcessingJob';
import { SkipEvent } from './domain/SkipEvent';
import { SkipsProcessingService } from './services/SkipsProcessingService';
import { getLocalAtSearch } from '../db_ops/airtable_search_ops';

export class SkipsProcessingDataProvider {
  private service = new SkipsProcessingService();

  async fetchJobsToProcess(): Promise<LocalAtRecord<JobsFieldSet>[]> {
    const query = `AND(
      {Exe # Samples Skipped} != BLANK(),
      {Testing?} != "Test",
      NOT(FIND('Skips - Code Ran', {Job Flags})),
      NOT(FIND('Skips - Reviewed', {Job Flags})),
      NOT(FIND('Skips - Final Approval', {Job Flags})),
      OR(
        {Exe # Samples Skipped} != 0,
        OR(
          {J Status OVRD} = "1X. Partial",
          {J Status OVRD} = "1X. Partial - Field Done"
        )
      )
    )`;

    try {
      const search = await getLocalAtSearch<JobsFieldSet>('Jobs', '', query, false);

      try {
        await search.refresh({ waitForAttachments: false });
      } catch (err) {
        console.error(`Could not refresh airtable Jobs.`, err);

        return [];
      }

      return search.getRecords();
    } catch (e) {
      console.error(e);

      return [];
    }
  }

  async fetchJobsCodeRan(): Promise<LocalAtRecord<JobsFieldSet>[]> {
    // "Job Flags" condition:
    // - "Skips - Code Ran" - is on
    // - "Skips - Reviewed" - is off and "Skips - Final Approval" - is off
    const query = `AND(
      FIND('Skips - Code Ran', {Job Flags}),
      NOT(FIND('Skips - Reviewed', {Job Flags})),
      NOT(FIND('Skips - Final Approval', {Job Flags}))
      )
    `;

    try {
      const search = await getLocalAtSearch<JobsFieldSet>('Jobs', '', query, false);

      try {
        await search.refresh({ waitForAttachments: false });
      } catch (err) {
        console.error(`Could not refresh airtable Jobs.`, err);

        return [];
      }

      return search.getRecords();
    } catch (e) {
      console.error(e);

      return [];
    }
  }

  async fetchSkipEvents(): Promise<SkipEvent[]> {
    const jobRecords = await this.fetchJobsCodeRan();
    const jobs = jobRecords.map((record) => new SkipsProcessingJob(record));

    // Fetch shift names for all jobs
    await this.loadShiftNames(jobs);

    return this.service.groupJobsIntoSkipEvents(jobs);
  }

  private async loadShiftNames(jobs: SkipsProcessingJob[]): Promise<void> {
    // Collect all unique shift IDs
    const shiftIds = new Set<string>();
    jobs.forEach((job) => {
      job.shift.forEach((shiftId) => shiftIds.add(shiftId));
    });

    if (shiftIds.size === 0) {
      return;
    }

    // Fetch all shifts at once
    const shiftIdsArray = Array.from(shiftIds);
    const query = `OR(${shiftIdsArray.map((id) => `RECORD_ID() = '${id}'`).join(',')})`;

    try {
      const search = await getLocalAtSearch<ShiftsFieldSet>('Shifts', '', query, false);
      await search.refresh({ waitForAttachments: false });
      const shiftRecords = search.getRecords();

      // Create a map of shift ID to shift name
      const shiftMap = new Map<string, string>();
      shiftRecords.forEach((record) => {
        const name = record.get('Name');

        if (name) {
          shiftMap.set(record.id, name);
        }
      });

      // Update job shift names
      jobs.forEach((job) => {
        if (job.shift.length > 0) {
          const shiftId = job.shift[0];
          const shiftName = shiftMap.get(shiftId);

          if (shiftName) {
            job.setShiftName(shiftName);
          }
        }
      });
    } catch (e) {
      console.error('Failed to load shift names:', e);
      // Continue without shift names
    }
  }
}
